import { cn } from '@/lib/utils';
import { FC, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
} from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface ImagePreviewProps {
  open: boolean;
  onClose: () => void;
  images: { fileId: string; base64: string; contentType: string }[];
  initialIndex?: number;
  onIndexChange?: (index: number) => void;
}

export const ImagePreview: FC<ImagePreviewProps> = ({
  open,
  onClose,
  images,
  initialIndex = 0,
  onIndexChange,
}) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const currentIndex =
    initialIndex >= 0 && initialIndex < images.length ? initialIndex : 0;
  const currentImage = images[currentIndex];

  const handlePrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    onIndexChange?.(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    onIndexChange?.(newIndex);
  };

  if (!open || images.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay className="z-[99]" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-[99] w-full size-fit translate-x-[-50%] translate-y-[-50%] flex flex-col p-0 bg-transparent border-0 shadow-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
          onEscapeKeyDown={onClose}
          onPointerDownOutside={onClose}
          onClick={(e) => e.stopPropagation()}
        >
          <VisuallyHidden>
            <DialogTitle>Rasm ko'rish</DialogTitle>
          </VisuallyHidden>

          <button
            onClick={onClose}
            className="absolute -right-10 -top-10 z-[100] rounded-full bg-white/90 hover:bg-white p-2 shadow-lg border border-gray-200 transition-all"
          >
            <X />
          </button>
          <img
            src={currentImage.base64}
            alt={`Rasm ${currentImage.fileId}`}
            className="h-[80vh] w-fit object-contain"
          />
          {images.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute -left-10 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg border border-gray-200 transition-all z-10"
              >
                <ChevronLeft />
              </button>
              <button
                onClick={handleNext}
                className="absolute -right-10 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg border border-gray-200 transition-all z-10"
              >
                <ChevronRight />
              </button>
            </>
          )}

          {images.length > 1 && (
            <div className="w-full flex gap-2 justify-center overflow-x-auto absolute -bottom-5">
              {images.map((file, index) => (
                <img
                  src={file.base64}
                  alt={`Thumbnail ${index + 1}`}
                  className="size-[100px] object-cover"
                  key={file.fileId}
                  onClick={() => onIndexChange?.(index)}
                />
              ))}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
